import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import styled from 'styled-components'
import { MetricCard } from '../../../../atoms/MetricCard'
import { useRentalMarket, type PriceMode } from '../../RentalMarketContext'

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 20px;

  @container (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @container (max-width: 380px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 16px;
  }
`

interface RentalMarketSummaryMetricsProps {
  t: TFunction
}

interface SummaryData {
  medianRent: number
  medianRentM2: number
  totalListings: number
}

export const RentalMarketSummaryMetrics: React.FC<
  RentalMarketSummaryMetricsProps
> = ({ t }) => {
  const { filters, service, priceMode } = useRentalMarket()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<SummaryData | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        // Calculate date range for last 12 months
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)

        const apiFilters = {
          deal_type: 'rent',
          property_category:
            filters.propertyCategory.length > 0
              ? filters.propertyCategory
              : undefined,
          location: locationValue,
          location_type: locationType,
          active_listing_timestamp: {
            min: startDate.toISOString().split('T')[0],
            max: endDate.toISOString().split('T')[0],
          },
          outlier_field: 'price',
          outlier_percentage: 2,
        }

        // Fetch distribution data to get summary metrics
        const response = await service.getRentalDistributionData(apiFilters)
        const aggs = response.aggregations

        // Extract median rent from price percentiles
        const medianRent =
          aggs?.price_percentiles?.values?.['50.0'] ||
          aggs?.price_stats?.avg ||
          0

        // Extract median rent/m² from price_m2 percentiles
        const medianRentM2 =
          aggs?.price_m2_percentiles?.values?.['50.0'] ||
          aggs?.price_m2_stats?.avg ||
          0

        // Get total listings count from rooms histogram buckets
        const roomsBuckets = aggs?.rooms_histogram?.buckets || []
        const totalListings = roomsBuckets.reduce(
          (sum: number, b: { doc_count: number }) => sum + b.doc_count,
          0
        )

        setData({
          medianRent,
          medianRentM2,
          totalListings,
        })
      } catch (err) {
        console.error('Error fetching rental market summary:', err)
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters, service])

  const formatRentM2 = (value: number, mode: PriceMode): string => {
    const adjustedValue = mode === 'yearly' ? value * 12 : value
    return `CHF ${adjustedValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }

  const formatRent = (value: number, mode: PriceMode): string => {
    const adjustedValue = mode === 'yearly' ? value * 12 : value
    if (adjustedValue >= 1000) {
      return `CHF ${Math.round(adjustedValue).toLocaleString()}`
    }
    return `CHF ${adjustedValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }

  if (loading || !data) {
    return (
      <MetricsGrid>
        <MetricCard
          title={t('rental.summary.medianRentM2.title')}
          value="—"
          subtitle={t('rental.summary.medianRentM2.subtitle')}
          variant="default"
        />
        <MetricCard
          title={t('rental.summary.medianRent.title')}
          value="—"
          subtitle={t('rental.summary.medianRent.subtitle')}
          variant="default"
        />
        <MetricCard
          title={t('rental.summary.marketVolume.title')}
          value="—"
          subtitle={t('rental.summary.marketVolume.subtitle')}
          variant="default"
        />
      </MetricsGrid>
    )
  }

  return (
    <MetricsGrid>
      <MetricCard
        title={t('rental.summary.medianRentM2.title')}
        value={formatRentM2(data.medianRentM2, priceMode)}
        subtitle={t('rental.summary.medianRentM2.subtitle', {
          period:
            priceMode === 'yearly'
              ? t('market.common.year')
              : t('market.common.month'),
        })}
        variant="default"
      />
      <MetricCard
        title={t('rental.summary.medianRent.title')}
        value={formatRent(data.medianRent, priceMode)}
        subtitle={t('rental.summary.medianRent.subtitle', {
          period:
            priceMode === 'yearly'
              ? t('market.common.year')
              : t('market.common.month'),
        })}
        variant="default"
      />
      <MetricCard
        title={t('rental.summary.marketVolume.title')}
        value={data.totalListings.toLocaleString()}
        subtitle={t('rental.summary.marketVolume.subtitle')}
        variant="default"
      />
    </MetricsGrid>
  )
}
